import { useEffect, useRef, useState } from 'react';
import { Eraser, Upload } from 'lucide-react';

interface Props {
  /** Volá se po každé změně: PNG data URL, nebo null, když je pole prázdné / podpis moc malý. */
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
}

const W = 900;
const H = 300;

/**
 * Pole pro nakreslení podpisu prstem, myší nebo perem, případně nahrání fotky podpisu.
 * Výsledek je PNG; server ho stejně znovu ověří a překóduje.
 */
export default function SignaturePad({ onChange, disabled }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const stats = useRef({ points: 0, minX: 1e9, maxX: -1e9 });
  const [hasInk, setHasInk] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { clear(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const ctx = () => ref.current!.getContext('2d')!;

  function clear(notify = true) {
    const c = ref.current; if (!c) return;
    ctx().clearRect(0, 0, W, H);
    stats.current = { points: 0, minX: 1e9, maxX: -1e9 };
    setHasInk(false); setErr('');
    if (notify) onChange(null);
  }

  function pos(e: React.PointerEvent<HTMLCanvasElement>) {
    const r = ref.current!.getBoundingClientRect();
    return { x: (e.clientX - r.left) * W / r.width, y: (e.clientY - r.top) * H / r.height };
  }

  function emit() {
    const s = stats.current;
    if (s.points > 25 && s.maxX - s.minX > 80) {
      setHasInk(true);
      onChange(ref.current!.toDataURL('image/png'));
    } else {
      setHasInk(false);
      onChange(null);
    }
  }

  function down(e: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true; last.current = pos(e);
    try { ref.current!.setPointerCapture(e.pointerId); } catch { /* nevadí */ }
    const c = ctx();
    c.fillStyle = '#111'; c.beginPath(); c.arc(last.current.x, last.current.y, 2.5, 0, Math.PI * 2); c.fill();
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || !last.current) return;
    e.preventDefault();
    const p = pos(e); const c = ctx();
    c.lineWidth = 5; c.lineCap = 'round'; c.lineJoin = 'round'; c.strokeStyle = '#111';
    c.beginPath(); c.moveTo(last.current.x, last.current.y); c.lineTo(p.x, p.y); c.stroke();
    last.current = p;
    const s = stats.current; s.points++; s.minX = Math.min(s.minX, p.x); s.maxX = Math.max(s.maxX, p.x);
  }
  function up() { if (!drawing.current) return; drawing.current = false; last.current = null; emit(); }

  /** Fotka podpisu na bílém papíře: tmavé tahy se převedou na černý podpis na průhledném pozadí. */
  function upload(file: File) {
    setErr('');
    if (!/^image\/(png|jpeg)$/.test(file.type) || file.size > 8_000_000) {
      setErr('Nahrajte obrázek PNG nebo JPG do 8 MB.'); return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(W / img.width, H / img.height, 1);
      const w = Math.max(1, Math.round(img.width * scale)); const h = Math.max(1, Math.round(img.height * scale));
      const off = document.createElement('canvas'); off.width = w; off.height = h;
      const oc = off.getContext('2d')!; oc.drawImage(img, 0, 0, w, h);
      const data = oc.getImageData(0, 0, w, h);
      let ink = 0;
      for (let i = 0; i < data.data.length; i += 4) {
        const g = 0.299 * data.data[i]! + 0.587 * data.data[i + 1]! + 0.114 * data.data[i + 2]!;
        const a = Math.max(0, Math.min(255, Math.round((200 - g) * 255 / 120)));
        data.data[i] = 17; data.data[i + 1] = 17; data.data[i + 2] = 17; data.data[i + 3] = a;
        if (a > 128) ink++;
      }
      if (ink < 150) { setErr('V obrázku není vidět podpis. Použijte tmavý podpis na světlém papíře.'); return; }
      oc.putImageData(data, 0, 0);
      const c = ref.current!; const cc = c.getContext('2d')!;
      cc.clearRect(0, 0, W, H);
      cc.drawImage(off, Math.round((W - w) / 2), Math.round((H - h) / 2));
      stats.current = { points: 999, minX: 0, maxX: W };
      setHasInk(true);
      onChange(c.toDataURL('image/png'));
    };
    img.onerror = () => { URL.revokeObjectURL(url); setErr('Obrázek se nepodařilo načíst.'); };
    img.src = url;
  }

  return (
    <div className="space-y-2">
      <canvas ref={ref} width={W} height={H}
              onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up}
              aria-label="Pole pro podpis"
              className="w-full bg-white border-2 border-dashed border-gray-300 rounded-xl touch-none cursor-crosshair"
              style={{ aspectRatio: '3 / 1' }} />
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => clear()} disabled={disabled || !hasInk}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-40">
          <Eraser className="h-3.5 w-3.5" /> Smazat
        </button>
        <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg text-gray-700 ${disabled ? 'opacity-40' : 'hover:bg-gray-50 cursor-pointer'}`}>
          <Upload className="h-3.5 w-3.5" /> Nahrát obrázek podpisu
          <input type="file" accept="image/png,image/jpeg" className="hidden" disabled={disabled}
                 onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
        </label>
        <span className="text-xs text-gray-400">Podepište se prstem, myší nebo perem do rámečku.</span>
      </div>
      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  );
}
